import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePagination } from '../hooks/usePagination';
import Pagination from './PaginationNew';
import { CommunityService, CommentService } from '../services/communityService';
import type { CommunityPost } from '../types/community';

const CommunityPostsTable: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { currentPage, itemsPerPage, setPage, setLimit } = usePagination({
    defaultPage: 1,
    defaultLimit: 20,
  });

  const [rows, setRows] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    document.title = currentPage > 1 ? `Community Posts - Page ${currentPage}` : 'Community Posts';
  }, [currentPage]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await CommunityService.listPosts({
          page: currentPage,
          limit: itemsPerPage,
          sort: '-createdAt',
          status: 'approved',
          search: debouncedSearch.trim() || undefined,
        });

        if (!isMounted) return;
        setRows(res.items || []);
        setTotalPages(res.pages || 1);
        setTotalItems(typeof res.total === 'number' ? res.total : (res.items || []).length);

        // Load comment counts for all posts
        const counts: Record<string, number> = {};
        for (const post of (res.items || [])) {
          try {
            const commentsRes = await CommentService.listComments({
              communityId: post._id,
              limit: 1,
            });
            counts[post._id] = commentsRes.total || 0;
          } catch (e) {
            counts[post._id] = 0;
          }
        }
        if (!isMounted) return;
        setCommentCounts(counts);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load community posts');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [currentPage, itemsPerPage, debouncedSearch]);

  const formatDate = (iso: string | undefined | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN');
  };

  const formatDateValue = (value: unknown) => {
    if (!value) return '—';
    if (typeof value === 'string') return formatDate(value);
    if (typeof value === 'object' && value && '$date' in (value as any) && typeof (value as any).$date === 'string') {
      return formatDate((value as any).$date);
    }
    return '—';
  };

  const formatCell = (value: unknown) => {
    if (value == null) return '—';
    if (typeof value === 'string') return value || '—';
    if (typeof value === 'number') return value.toLocaleString('en-IN');
    return '—';
  };

  const getUserLabel = (post: CommunityPost): string => {
    const v: any = post.userId ?? post.user;
    if (!v) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      const name = typeof v.name === 'string' ? v.name.trim() : '';
      const email = typeof v.email === 'string' ? v.email.trim() : '';
      if (name && email) return `${name} (${email})`;
      if (name) return name;
      if (email) return email;
    }
    return '—';
  };

  const safeRows = useMemo(() => rows, [rows]);

  if (loading && safeRows.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading community posts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Community Posts</h1>
          <p className="mt-1 text-sm text-gray-500">Approved community discussion posts</p>
        </div>

        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (currentPage !== 1) setPage(1);
              }}
              className="block w-80 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Where</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeRows.map((post) => (
                <tr key={post._id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900">{formatCell(post.title)}</div>
                    <div className="text-xs text-gray-500 break-all">{post._id}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">₹{formatCell(post.amount)}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">{formatCell(post.where)}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">{getUserLabel(post)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{commentCounts[post._id] || 0}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDateValue(post.createdAt)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      onClick={() => navigate(`/community/${encodeURIComponent(String(post._id))}`)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}

              {!loading && safeRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                    No community posts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setPage}
        onLimitChange={setLimit}
      />
    </div>
  );
};

export default CommunityPostsTable;
